import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { MessageSquareWarning } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * The bug reporter, as a button in the nav rather than Sentry's floating one.
 *
 * The floating launcher is fixed, so as the window narrows it ends up on top
 * of whatever moves underneath it - on the event screen, the Sign Up button.
 * `autoInject: false` in main.tsx turns it off.
 *
 * Renders hidden until Sentry hands over the integration: the button has to
 * exist for `attachTo` to be given a node, and a button that opens nothing is
 * the defect this replaces. Stays hidden when Sentry is not initialised.
 */
export function FeedbackButton({
  className,
  iconOnly,
}: {
  className?: string;
  /** Label hidden, for the compact header where space is the constraint. */
  iconOnly?: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const feedback = Sentry.getFeedback();
    if (!feedback || !ref.current) return;
    setReady(true);
    return feedback.attachTo(ref.current);
  }, []);

  return (
    <Button
      ref={ref}
      type="button"
      variant="outline"
      size={iconOnly ? "icon" : "default"}
      aria-label={iconOnly ? "Report a Bug" : undefined}
      className={cn(
        "flex items-center gap-2",
        ready ? undefined : "hidden",
        className,
      )}
    >
      <MessageSquareWarning className="h-4 w-4" />
      {!iconOnly && "Report a Bug"}
    </Button>
  );
}

export default FeedbackButton;
